/**
 *  版权所有 © 2026 东方闻道 All Rights Reserved
 *
 *  创建者: wangchao
 *  创建时间: 2026-09-01 10:30
 *  文件名: src/components/ppt-learning-plan-viewer/download-button.tsx
 *  功能描述: 文件下载按钮组件，传入 fileId 与资源上下文，点击即可下载对应文件；
 *           下载地址获取方式与 LessonItem/DownloadItem 保持一致（资源中心下载接口）
 */
import React, { useState } from 'react';
import { Button, message } from 'antd';
import AxiosWrap from '../../lib/request';
import api from '../../config/api/index';
import { downloadFileByALink, downloadFileByBlob } from '../../util/file';
import { LearningPlanPreviewContext } from './types';
import { apiGetApiFilesId } from '../../apis/file-service/file-query-controller-v3';
import { url } from 'eastedu-kit';

interface FileDownloadButtonProps {
  /** 待下载文件的 fileId */
  fileId: string;
  /** 资源上下文，用于构造资源中心下载接口（与 DownloadItem 的下载方式一致） */
  previewContext: LearningPlanPreviewContext;
  /** 追加到按钮上的类名，供调用方控制位置等样式 */
  className?: string;
  /** 按钮文案，默认「下载」 */
  children?: React.ReactNode;
  /** 下载前执行的异步任务，例如等待编辑器将当前修改发送至 DS */
  prepareDownload?: () => Promise<string | null>;
  /** 下载成功回调（fileName 为下载使用的文件名），供调用方做埋点等后续处理 */
  onDownloadComplete?: (fileName: string) => void;
}

type DownloadStatus = 'saving' | 'downloading';

/**
 * 文件下载按钮：点击时经资源中心接口换取下载地址（cdnUrl 优先），
 * blob 方式下载以固定文件名，blob 拉取失败（如 CORS）时降级为直接下载
 */
function FileDownloadButton(props: FileDownloadButtonProps) {
  const { fileId, previewContext, className, children, prepareDownload } = props;
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus | null>(null);
  const [downloadPercent, setDownloadPercent] = useState(0);

  /** 等待编辑内容保存后下载文件。 */
  async function handleDownload() {
    if (downloadStatus) return;
    setDownloadStatus('saving');

    let prepareResultFileId: string | null = null;

    try {
      if (prepareDownload) {
        prepareResultFileId = await prepareDownload();
      }
    } catch (error) {
      console.error(error);
      message.error('文档保存失败，请稍后重试');
      setDownloadStatus(null);
      return;
    }

    setDownloadStatus('downloading');
    setDownloadPercent(0);
    try {
      const fileName = previewContext.wordFileName;
      const requestUrl = `${api.api}/resources/${previewContext.resourceId}/files/${fileId}/download`;
      // 查询参数由 axios 自动编码序列化，无需手动 encodeURIComponent
      const res = await (AxiosWrap as any)({
        url: requestUrl,
        method: 'get',
        params: {
          courseId: previewContext.courseId,
          lessonId: previewContext.lessonId,
          fileName,
          originalFileId: previewContext.originalFileId || '',
          documentId: previewContext.documentId || '',
        },
      });

      let downloadUrl = res?.data?.cdnUrl || res?.data?.url;

      if (prepareResultFileId) {
        const result = await apiGetApiFilesId({ id: prepareResultFileId });
        const prepareDownloadUrl = result.urls?.cdn ?? result.urls?.download;
        if (prepareDownloadUrl) {
          downloadUrl = url.appendQuery(prepareDownloadUrl, {
            t: result.md5, // 文档编辑之后cdn不会更新，需要增加一个参数让cdn重新回源
          });
        }
      }

      if (!downloadUrl) {
        message.error('该文件暂时无法下载，请联系客服');
        return;
      }
      // 与 DownloadItem 保持一致：强制 https
      downloadUrl = `https${downloadUrl.substring(downloadUrl.indexOf(':'))}`;
      try {
        await downloadFileByBlob(downloadUrl, fileName, setDownloadPercent);
      } catch (blobErr) {
        console.error(blobErr);
        downloadFileByALink(downloadUrl, fileName);
      }
      props.onDownloadComplete && props.onDownloadComplete(fileName);
    } catch (err) {
      console.error(err);
      message.error('该文件暂时无法下载，请联系客服');
    } finally {
      setDownloadStatus(null);
      setDownloadPercent(0);
    }
  }

  return (
    <Button
      className={className}
      type="primary"
      icon="download"
      loading={downloadStatus !== null}
      onClick={handleDownload}
    >
      {(() => {
        if (downloadStatus === 'saving') return '保存中...';
        if (downloadStatus === 'downloading' && downloadPercent > 0) {
          return `下载中 ${downloadPercent}%`;
        }
        return children ?? '下载';
      })()}
    </Button>
  );
}

export { FileDownloadButton };
